import { Box, Grid, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { gameTransitionType, SPACING_LG, SPACING_SM, SPACING_XS } from "../../configs/constants";
import { useDialogState } from "../../stores/states/useDialogState";
import { StyledFillButton } from "../../styled/filled_button";
import { sxCenter } from "../styled/styled_flex_container";
import PromptTypo from "../typography/typography_prompt";
import { SwalConfirmation, SwalLoading, SwalCompleted } from "./swal_alert";
import { ZustandDialogContainer } from "./zustand_dialog";
import React from "react";
import { MaskedInput } from "rsuite";
import gameService, { GameSession, WinningNumber } from "../../services/game_service";
import { CheckNumberForm, fieldLabels, useCheckNumberFormState } from "../../stores/states/useCheckNumberState";
import { rxNumberOnly } from "../../configs/regex";

interface CheckNumberComponentProps {
  onSubmitCheckNumber?: (targetStatus: gameTransitionType) => Promise<null | undefined>;
  game?: GameSession;
}

const CheckNumberComponent = React.memo((
  {
    onSubmitCheckNumber = async (targetStatus) => undefined,
    game = undefined,
  }: CheckNumberComponentProps
): JSX.Element => {
  const { closeDialog, isOpen } = useDialogState();
  const { form, setForm, mapWinningResponseToForm, mapFormToWinningPayload } = useCheckNumberFormState();
  const [gameIdState, setGameIdState] = useState<string | undefined>(undefined)
  const formRef = useRef<CheckNumberForm>(form ?? {} as CheckNumberForm);
  const [formErrors, setFormErrors] = useState<Record<keyof CheckNumberForm, boolean>>(() => {
    const initialErrors: Record<keyof CheckNumberForm, boolean> = {} as Record<keyof CheckNumberForm, boolean>;
    Object.keys(form ?? {}).forEach((key) => {
      initialErrors[key as keyof CheckNumberForm] = false;
    });
    return initialErrors;
  });



  const handleSubCheckNumber = useCallback(async () => {
    SwalConfirmation({
      title: 'ยืนยันการตรวจเลข',
      description: 'การตรวจเลขจะทำการคำนวนเครดิตเพื่อนำไปหักในภายหลัง?',
    }).then((result) => {
      if (result.isConfirmed && form && game) {
        closeDialog();
        const currentForm = formRef.current;
        const mapStateToPayload = mapFormToWinningPayload(currentForm);

        gameService.updateGamePrize(`${game.id}`, mapStateToPayload).then(async res => {
          if (!!res) {
            await onSubmitCheckNumber?.(gameTransitionType.checkNumber);
            SwalCompleted({ title: 'ตรวจเลขสำเร็จ' });

            SwalLoading(
              async () => onSubmitCheckNumber?.(gameTransitionType.checkNumber), // call เพื่อลื่อน state
              { title: 'กำลังทำรายการ' }
            );
          }
        })
      }
    });
  }, [closeDialog]);

  useEffect(() => {
    setGameIdState(game && form ? JSON.stringify(mapWinningResponseToForm(form as unknown as WinningNumber)) : undefined);
  }, [])

  useEffect(() => {
    formRef.current = form ?? {} as CheckNumberForm;
  }, [form]);

  useEffect(() => {
    const newFormErrors: Partial<Record<keyof CheckNumberForm, string>> = {};

    Object.entries(form ?? {}).forEach(([key, value]) => {
      const trimmedValue = String(value ?? '').trim().replace('%', '');
      const isNumeric = !isNaN(Number(trimmedValue));
      const { length } = value;

      if (key === 'threeDigitUpper') {
        newFormErrors.threeDigitUpper = (isNumeric && length === 3) ? '' : 'format error'
      }
      else if (key === 'twoDigitLower') {
        newFormErrors.twoDigitLower = (isNumeric && length === 2) ? '' : 'format error'
      }
      else if (key === 'threeDigitLower') {
        const hasAnyUnderScore = form?.threeDigitLower.replace('_', '').length !== 15;
        newFormErrors.threeDigitLower = hasAnyUnderScore ? 'format error' : ''
      }
      else {
        newFormErrors[key as keyof CheckNumberForm] = isNumeric ? '' : 'format error';
      }
    });

    setFormErrors(
      Object.keys(newFormErrors).reduce((acc, key) => {
        acc[key as keyof CheckNumberForm] = !!newFormErrors[key as keyof CheckNumberForm];
        return acc;
      }, {} as Record<keyof CheckNumberForm, boolean>)
    );
  }, [form]);


  const handleOnChange = (
    value: string,
    fieldName: string
  ) => {
    const rawValue = value
    const newForm = {
      ...form,
      [fieldName]: rawValue,
    };
    const parts = rawValue.split('-').map(p => p.trim());

    if (parts.length === 4) {
      newForm.threeLowerWinningNumber1 = parts[0];
      newForm.threeLowerWinningNumber2 = parts[1];
      newForm.threeLowerWinningNumber3 = parts[2];
      newForm.threeLowerWinningNumber4 = parts[3];
    }

    setForm(newForm);
  }

  const isFormValid = Object.values(formErrors).every(error => !error);

  return <ZustandDialogContainer
    sx={{ maxWidth: '600px', justifySelf: 'center' }}
    hasCloseIcon
    title="ตรวจเลข"
    component={Box}
    padding="24px 0px"
    open={isOpen}
    footer={<Box sx={{ ...sxCenter, padding: `${SPACING_LG} ${SPACING_LG}` }}>
      <StyledFillButton
        type='submit'
        sx={{
          bgcolor: (theme) => theme.palette.secondary.main,
          width: "calc(100% - 120px)",
          ":hover": { color: (theme) => theme.palette.secondary.light }
        }}
        onClick={handleSubCheckNumber}
        disabled={!isFormValid}
      >
        บันทึก
      </StyledFillButton>
    </Box>}
  >
    <Grid container spacing={2} sx={{ width: '100%', padding: SPACING_LG }}>
      {Object.keys(form ?? {}).map((fieldName, index) => (
        <React.Fragment key={index}>
          {index === 0 && (
            <Grid item xs={12}>
              <PromptTypo variant='subtitle1' fontWeight='bold'>เลขที่ออก</PromptTypo>
              <Box
                sx={{
                  width: '100%',
                  height: '2px',
                  bgcolor: (theme) => `${theme.palette.secondary.light}`,
                  borderRadius: '4px',
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={index === 2 ? 12 : 6}>
            {(index <=12) &&
              <PromptTypo variant='subtitle2' sx={{ paddingBottom: 0, paddingLeft: SPACING_SM, fontWeight: 'bold' }}>
              {fieldLabels[fieldName as keyof CheckNumberForm] || fieldName}
            </PromptTypo>
            }
            {(index === 0 || index === 1) &&
              <TextField
                fullWidth
                name={fieldName}
                value={form?.[fieldName as keyof CheckNumberForm] ?? ''}
                onChange={(e) => handleOnChange(e.target.value, fieldName)}
                placeholder={`${fieldLabels[fieldName as keyof CheckNumberForm] || fieldName}`}
                variant="outlined"
                size="small"
                error={formErrors[fieldName as keyof CheckNumberForm] || false}
                helperText={formErrors[fieldName as keyof CheckNumberForm] ? 'ข้อมูลไม่ถูกต้อง' : undefined}
                slotProps={{
                  input: {
                    inputProps: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: index === 1 ? 2 : 3,
                    }
                  }
                }}
                sx={{
                  padding: `${SPACING_XS} ${SPACING_SM}`,
                }}
              />
            }

            {index === 2 &&
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="111-222-333-444"
                value={form?.[fieldName as keyof CheckNumberForm] ?? ''} // Safely bind the value from the form state
                onChange={(e) => {
                  handleOnChange(`${e}`, fieldName)
                }}
                error={formErrors[fieldName as keyof CheckNumberForm] || false}
                helperText={formErrors[fieldName as keyof CheckNumberForm] ? 'ข้อมูลไม่ถูกต้อง' : undefined}
                InputProps={{
                  inputComponent: MaskedInput as any,
                  inputProps: {
                    mask: [
                      /\d/, /\d/, /\d/, '-',
                      /\d/, /\d/, /\d/, '-',
                      /\d/, /\d/, /\d/, '-',
                      /\d/, /\d/, /\d/
                    ],
                    guide: true,
                    placeholder: "111-222-333-444",
                  },
                }}
                sx={{
                  padding: `${SPACING_XS} ${SPACING_SM}`,
                }}
              />
            }

            {(index > 2 && index <=12) && (
              <TextField
                fullWidth
                name={fieldName}
                value={form?.[fieldName as keyof CheckNumberForm] ?? ''}
                onChange={(event) => {
                  let value = event.target.value.replace(' %', ''); // Remove '%' for validation
                  value = value.replace(/[^0-9]/g, ''); // Allow only numeric characters
                  if (!isNaN(Number(value))) {
                    value = Math.min(100, Math.max(0, Number(value))).toString(); // Clamp value between 0 and 100
                  }
                  handleOnChange(value, fieldName)
                }}
                onBlur={(event) => {
                  let value = event.target.value.replace(' %', ''); // Remove '%' for validation
                  value = value.replace(/[^0-9]/g, ''); // Allow only numeric characters
                  if (!isNaN(Number(value))) {
                    value = Math.min(100, Math.max(0, Number(value))).toString(); // Clamp value between 0 and 100
                  }
                  setForm({ ...form, [fieldName]: `${value} %` }); // Append '%' on blur
                }}
                placeholder={`${fieldLabels[fieldName as keyof CheckNumberForm] || fieldName}`}
                variant="outlined"
                error={formErrors[fieldName as keyof CheckNumberForm] || false}
                helperText={formErrors[fieldName as keyof CheckNumberForm] ? 'ข้อมูลไม่ถูกต้อง' : undefined}
                size="small"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 3,
                }}
                sx={{
                  padding: `${SPACING_XS} ${SPACING_SM}`,
                }}
              />
            )}
          </Grid>
          {index === 2 && (
            <Grid item xs={12}>
              <PromptTypo variant='subtitle1' fontWeight='bold'>อัตราจ่าย %</PromptTypo>
              <Box
                sx={{
                  width: '100%',
                  height: '2px',
                  bgcolor: (theme) => `${theme.palette.secondary.light}`,
                  borderRadius: '4px',
                }}
              />
            </Grid>
          )}
        </React.Fragment>
      ))}
    </Grid>
  </ZustandDialogContainer>
});

export default CheckNumberComponent;